import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  getMemberAttendanceLogs,
  getLogHistory,
  deleteAttendanceLog,
  downloadLogs,
} from "../services/attendanceService";
import { getMember, updateMember, sendReminder as sendReminderApi } from "../services/memberService";
import { getActiveBreaks } from "../services/statisticsService";
import { useSettings } from "../contexts/SettingsContext";
import { secToHhmm, liveBreakSeconds, liveActualSeconds } from "../utils/liveHours";
import { WORK_NATURE_OPTIONS, WORK_NATURE_LABELS } from "../enums/workNature";
import useConfirm from "../hooks/useConfirm";
import ConfirmModal from "../components/ui/ConfirmModal";
import EditEntryModal from "../components/attendance/EditEntryModal";
import { useBreakDetails, BreakCell, BreakDetailRow } from "../components/attendance/BreakDetails";
import { useReminderDetails, ReminderCell, ReminderDetailRow } from "../components/attendance/ReminderDetails";
import HoursCell from "../components/attendance/HoursCell";
import Skeleton, { SkeletonRows } from "../components/ui/Skeleton";
import "../assets/css/components/date-filter.css";
import "../assets/css/pages/member-detail.css";

const fmtTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm a") : null);
// Overnight record: check-out happened on a later calendar date than check-in. Computed on
// the backend (DATE(checkout) > DATE(checkin)) since the frontend only receives time-only values.
const isOvernight = (l) => !!l.isOvernight;
const secToHm = (sec) => {
  if (!sec) return "0h";
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
};
const timeToSec = (t) => {
  if (!t) return 0;
  const [h, m, s] = String(t).split(":").map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
};
// liveStatus / manual status → identity pill.
const liveBits = (m) => {
  if (m.status === "DEACTIVATED") return { label: "Deactivated", cls: "absent" };
  switch (m.liveStatus) {
    case "online": return { label: "Present", cls: "present" };
    case "remote": return { label: "Remote", cls: "present" };
    case "break": return { label: "On break", cls: "break" };
    case "deactivated": return { label: "Deactivated", cls: "absent" };
    default: return { label: "Offline", cls: "absent" };
  }
};
// per-day log status (override wins, else derived from times).
const logStatus = (log) => {
  const map = { PRESENT: ["Present", "present"], LATE: ["Late", "late"], ON_BREAK: ["On break", "break"], ABSENT: ["Absent", "absent"] };
  if (log.status && map[log.status]) return map[log.status];
  if (!log.checkIn) return ["Absent", "absent"];
  return log.isLate ? ["Late", "late"] : ["Present", "present"];
};

// Quick-pick chips. A custom date range (the calendar trigger beside them) is a fourth,
// implicit period — CUSTOM — selected by picking dates rather than clicking a chip.
const PERIODS = ["Last 30 days", "This month", "All"];
const CUSTOM = "Custom";

// Individual working-hours card — one schedule row per weekday. WH_DAYS index + 1 is the
// MySQL DAYOFWEEK number the backend stores (Sun=1 … Sat=7).
const WH_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const toTimeInput = (t) => (t ? String(t).slice(0, 5) : ""); // "09:00:00" -> "09:00"

// Build the { Sun:{enabled,start,end}, … } form map for the weekday grid.
// - If the member already has a saved per-day schedule, use it (missing weekdays = off).
// - Otherwise (first time enabling), MIRROR the org's Working Days: the org's working days
//   turn on with the org default start/end, the rest show as "Unavailable".
const seedDaySchedules = (list, org = {}) => {
  const saved = Array.isArray(list) ? list : [];
  if (saved.length) {
    const byDow = {};
    saved.forEach((s) => { byDow[s.weekday] = s; });
    const out = {};
    WH_DAYS.forEach((tok, i) => {
      const s = byDow[i + 1];
      out[tok] = s
        ? { enabled: !!s.enabled, start: toTimeInput(s.dayStart) || "09:00", end: toTimeInput(s.dayEnd) || "18:00" }
        : { enabled: false, start: "09:00", end: "18:00" };
    });
    return out;
  }
  let orgDays = org?.working_days;
  if (typeof orgDays === "string") { try { orgDays = JSON.parse(orgDays); } catch (e) { orgDays = null; } }
  if (!Array.isArray(orgDays)) orgDays = ["Sun", "Mon", "Tue", "Wed", "Thu"];
  const start = toTimeInput(org?.day_start) || "09:00";
  const end = toTimeInput(org?.day_end) || "18:00";
  const out = {};
  WH_DAYS.forEach((tok) => {
    out[tok] = { enabled: orgDays.includes(tok), start, end };
  });
  return out;
};

const MemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [logs, setLogs] = useState([]);
  const [onBreak, setOnBreak] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("Last 30 days");
  // Custom date range (react-date-range shape, same as the History page). Only in force
  // while `period === CUSTOM`; the chips and the calendar are mutually exclusive.
  const [dateRange, setDateRange] = useState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Unscoped recent logs, kept so the "This week" chart stays correct even while the
  // table below is filtered to an older range.
  const [weekLogs, setWeekLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  // Absent placeholder rows (no record that day) for the active period, from the History endpoint.
  const [absentRows, setAbsentRows] = useState([]);
  // The member's Slack user id, which creating a record needs; the profile payload lacks it,
  // so it's picked up from the History rows. Keyed by member so a stale id is never reused.
  const [slackRef, setSlackRef] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // bumped after "Add entry" to refetch everything
  const breakDetails = useBreakDetails(); // expandable break/back breakdown per record
  const reminderDetails = useReminderDetails(); // expandable reminder-type breakdown per record
  const [reminding, setReminding] = useState(false);
  const [savingNature, setSavingNature] = useState(false);
  const [hours, setHours] = useState(null); // individual working-hours form (null until member loads)
  const [savingHours, setSavingHours] = useState(false);
  const { settings: orgSettings, loading: settingsLoading } = useSettings(); // org defaults for first-time seed
  const { confirm, modalProps } = useConfirm();

  // Dates the SERVER should scope the query to. The endpoint otherwise returns only the
  // latest handful of records, so an older custom range has to be asked for explicitly.
  // "All" sends nothing and keeps the default recent window.
  const apiRange = useMemo(() => {
    const fmt = (d) => moment(d).format("YYYY-MM-DD");
    if (period === CUSTOM) return { startDate: fmt(dateRange[0].startDate), endDate: fmt(dateRange[0].endDate) };
    if (period === "Last 30 days") return { startDate: fmt(moment().subtract(30, "days")), endDate: fmt(moment()) };
    if (period === "This month") return { startDate: fmt(moment().startOf("month")), endDate: fmt(moment()) };
    return {}; // All
  }, [period, dateRange]);
  const { startDate: rangeStart, endDate: rangeEnd } = apiRange;

  // Only the very first load of a member may show the skeleton. A skeleton swaps out the
  // whole profile — rec-tools included — which would unmount the open date picker in the
  // middle of a selection: the calendar would vanish after the first click and reopen with
  // a complete start === end range, so the next click starts over and a multi-day range
  // could never be picked.
  const loadedIdRef = useRef(null);

  useEffect(() => {
    const range = rangeStart && rangeEnd ? { startDate: rangeStart, endDate: rangeEnd } : {};
    const firstForMember = loadedIdRef.current !== id;
    loadedIdRef.current = id;
    // Picking a range fires this twice — react-date-range's first click yields
    // start === end, the second completes it. Without this guard the two responses can
    // land out of order and the stale single-day result overwrites the real range.
    let cancelled = false;
    // `silent` skips the skeleton so the background poll doesn't flash the page.
    const load = async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      const [m, l, breaks] = await Promise.all([
        getMember(id),
        getMemberAttendanceLogs(id, range),
        getActiveBreaks(),
      ]);
      // A scoped fetch can exclude the current week entirely, so the week chart needs its
      // own unscoped copy; with no range in play the two requests would be identical.
      const wl = range.startDate ? (await getMemberAttendanceLogs(id)) ?? [] : l ?? [];
      if (cancelled) return;
      setMember(m);
      setLogs(l ?? []);
      setOnBreak(breaks?.onBreak ?? []);
      setWeekLogs(wl);
      setLoading(false); // unconditional: a superseded non-silent load must not strand the skeleton
    };
    // Debounced on range changes, like the History filter: the intermediate single-day
    // selection never reaches the network, only the completed range does.
    const first = setTimeout(() => load({ silent: !firstForMember }), firstForMember ? 0 : 300);
    // Poll so the live break / actual-hrs counters advance and self-correct on break end.
    const t = setInterval(() => load({ silent: true }), 30000);
    return () => { cancelled = true; clearTimeout(first); clearInterval(t); };
  }, [id, rangeStart, rangeEnd, reloadKey]);

  // Seed the individual working-hours form once the member loads. Guarded so the 30s
  // background poll never clobbers in-progress edits.
  useEffect(() => {
    // Wait for org settings to finish loading so a first-time seed can mirror the org
    // Working Days (otherwise a member with no saved schedule would show all days off).
    if (member && !settingsLoading && hours === null) {
      // Default from the GLOBAL settings (like the weekly schedule mirrors org working days),
      // so an unset field shows/uses the company value; a saved member override wins.
      setHours({
        enabled: !!member.customHoursEnabled,
        grace_period_min: member.gracePeriodMin ?? orgSettings?.grace_period_min ?? 15,
        day_schedules: seedDaySchedules(member.daySchedules, orgSettings),
        reminder_signin_after_minutes: member.reminderSigninAfterMinutes ?? orgSettings?.reminder_signin_after_minutes ?? 60,
        reminder_long_break_min: member.reminderLongBreakMin ?? orgSettings?.reminder_long_break_min ?? 60,
        reminder_signout_hours: member.reminderSignoutHours ?? orgSettings?.reminder_signout_hours ?? 10,
        reminder_signin_enabled: member.reminderSigninEnabled ?? orgSettings?.reminder_signin_enabled ?? true,
        reminder_long_break_enabled: member.reminderLongBreakEnabled ?? orgSettings?.reminder_long_break_enabled ?? true,
        reminder_signout_enabled: member.reminderSignoutEnabled ?? orgSettings?.reminder_signout_enabled ?? true,
      });
    }
  }, [member, hours, settingsLoading, orgSettings]);

  // Period-scoped set driven by the active rec-tools chip. Feeds BOTH the Recent-attendance
  // table and the stat-row below, so the tiles and the list always agree and update together.
  const filtered = useMemo(() => {
    const now = moment();
    return logs.filter((l) => {
      const d = moment(l.workDate);
      if (period === CUSTOM) return d.isBetween(moment(rangeStart), moment(rangeEnd), "day", "[]");
      if (period === "Last 30 days") return d.isSameOrAfter(moment().subtract(30, "days"), "day");
      if (period === "This month") return d.isSame(now, "month");
      return true; // All
    });
  }, [logs, period, rangeStart, rangeEnd]);

  // Dates to look for absent days in: the active period, capped at yesterday — today isn't
  // called absent while the member may still sign in. "All" only loads the latest records,
  // so it starts from the oldest of those.
  const absentRange = useMemo(() => {
    const yesterday = moment().subtract(1, "day");
    let start;
    let end = yesterday;
    if (period === CUSTOM) {
      start = moment(rangeStart);
      if (moment(rangeEnd).isBefore(yesterday, "day")) end = moment(rangeEnd);
    } else if (period === "Last 30 days") start = moment().subtract(30, "days");
    else if (period === "This month") start = moment().startOf("month");
    else {
      const oldest = logs.reduce((min, l) => (!min || l.workDate < min ? l.workDate : min), null);
      if (!oldest) return null;
      start = moment(oldest);
    }
    if (start.isAfter(end, "day")) return null;
    return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
  }, [period, rangeStart, rangeEnd, logs]);
  const absentStart = absentRange?.startDate;
  const absentEnd = absentRange?.endDate;

  // Absent days come from the History endpoint so the profile follows exactly the History
  // page's rule: active member, a working day for them (personal schedule or the org's
  // working days), and no record that day. Fetched when the range changes, not on the poll.
  useEffect(() => {
    if (!absentStart || !absentEnd) {
      setAbsentRows([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const found = [];
      let slackUserId = null;
      for (let page = 1; page <= 20; page++) {
        const { status, logs: pageLogs, pagination } = await getLogHistory({
          startDate: absentStart,
          endDate: absentEnd,
          memberIds: String(id),
          page,
          pageSize: 500,
        });
        if (status !== 200) break;
        for (const l of pageLogs || []) {
          slackUserId = slackUserId || l.slackUserId;
          if (!l.id) found.push(l); // placeholder row = no record that day
        }
        if (!pagination || page >= pagination.totalPages) break;
      }
      if (cancelled) return;
      setAbsentRows(found);
      if (slackUserId) setSlackRef({ id, slackUserId });
    })();
    return () => { cancelled = true; };
  }, [id, absentStart, absentEnd, reloadKey]);

  // Recent-attendance rows: the period's records plus its absent days, newest first. A day
  // that has a record never shows as absent (e.g. right after "Add entry", before the refetch).
  const rows = useMemo(() => {
    const recorded = new Set(logs.map((l) => l.workDate));
    const absent = absentRows.filter((l) => String(l.memberId) === String(id) && !recorded.has(l.workDate));
    return [...filtered, ...absent].sort((a, b) => b.workDate.localeCompare(a.workDate));
  }, [filtered, absentRows, logs, id]);

  // Label for the trigger button, e.g. "Jun 1 – Jun 30, 2026" (History uses the same shape).
  const dateRangeLabel = () => {
    const s = moment(dateRange[0].startDate), e = moment(dateRange[0].endDate);
    if (s.isSame(e, "day")) return s.format("MMM D, YYYY");
    return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  };

  // Human-readable scope label for the stat-row sub-text, driven by the active period.
  const periodLabel =
    period === CUSTOM
      ? dateRangeLabel()
      : { "Last 30 days": "last 30 days", "This month": "this month", All: "all time" }[period] || "this period";

  const stats = useMemo(() => {
    // Weekday span of the active period, for the attendance-rate denominator.
    // A custom range ends on its own end date; every other period runs up to today.
    const end = period === CUSTOM ? moment(rangeEnd) : moment();
    let start;
    if (period === CUSTOM) start = moment(rangeStart);
    else if (period === "This month") start = moment().startOf("month");
    else if (period === "Last 30 days") start = moment().subtract(30, "days");
    else start = filtered.reduce((min, l) => { const d = moment(l.workDate); return !min || d.isBefore(min) ? d : min; }, null) || moment();
    let weekdays = 0;
    const d = moment(start).startOf("day");
    while (d.isSameOrBefore(end, "day")) {
      const day = d.day();
      if (day !== 5 && day !== 6) weekdays++;
      d.add(1, "day");
    }
    const present = filtered.filter((l) => l.checkIn); // days actually attended in the period
    const presentDays = present.length;
    const rate = weekdays ? Math.min(100, Math.round((presentDays / weekdays) * 100)) : 0;
    const ins = present.map((l) => timeToSec(l.checkIn));
    const avgIn = ins.length ? Math.round(ins.reduce((a, b) => a + b, 0) / ins.length) : 0;
    const avgInStr = avgIn ? moment(`${String(Math.floor(avgIn / 3600)).padStart(2, "0")}:${String(Math.floor((avgIn % 3600) / 60)).padStart(2, "0")}`, "HH:mm").format("hh:mm a") : "—";
    const hoursSec = filtered.reduce((a, l) => a + timeToSec(l.actualWorkingHour || l.workingHour), 0);
    const late = filtered.filter((l) => l.isLate).length;
    const remoteDays = present.filter((l) => l.workType === "REMOTE").length;
    const officeDays = presentDays - remoteDays; // non-remote attended days
    const avgHours = presentDays ? secToHm(Math.round(hoursSec / presentDays)) : "—";
    const breakSec = filtered.reduce((a, l) => a + timeToSec(l.totalBreakDuration), 0);
    const breakCount = filtered.reduce((a, l) => a + (l.breakCount || 0), 0);
    // Total reminders (auto + manual) this member received over the period.
    const reminderCount = filtered.reduce((a, l) => a + (l.reminderCount || 0), 0);
    // Sessions the member forgot to sign out of. Server-resolved (missedSignOut) rather than
    // re-derived here, because the fact is now PERSISTED: an admin filling in the missing
    // sign-out afterwards must not erase it — the tile tracks discipline, not open cleanup.
    // Same flag the History KPI aggregates, so the two pages cannot disagree.
    const missedSignOuts = filtered.filter((l) => !!l.missedSignOut).length;
    return { rate, avgInStr, hours: secToHm(hoursSec), late, presentDays, remoteDays, officeDays, avgHours, breakHours: secToHm(breakSec), breakCount, reminderCount, missedSignOuts };
  }, [filtered, period, rangeStart, rangeEnd]);

  const week = useMemo(() => {
    const start = moment().subtract(moment().day(), "days"); // Sunday of this week (work week Sun–Thu)
    const byDate = {};
    // weekLogs, not the range-filtered logs: "This week" always means the current week.
    weekLogs.forEach((l) => (byDate[l.workDate] = l));
    return Array.from({ length: 5 }).map((_, i) => {
      const day = moment(start).add(i, "days");
      const log = byDate[day.format("YYYY-MM-DD")];
      const hrs = log ? timeToSec(log.actualWorkingHour || log.workingHour) / 3600 : 0;
      return { day: day.format("ddd"), h: Math.min(100, Math.round((hrs / 8) * 100)) };
    });
  }, [weekLogs]);

  const applyUpdatedLog = (updated) => setLogs((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  const handleDelete = async (log) => {
    const ok = await confirm({ title: "Delete attendance log?", message: "This permanently removes this check-in/out record." });
    if (!ok) return;
    const { status } = await deleteAttendanceLog(log.id);
    if (status === 200) { setLogs((prev) => prev.filter((l) => l.id !== log.id)); toast.success("Log deleted"); }
    else toast.error("Could not delete log");
  };
  const openEdit = (log) => setEditing({ ...log, memberName: member.memberName, memberDisplayName: member.memberName, memberTitle: member.memberTitle, memberImgUrl: member.memberImgUrl });
  // "Add entry": from an absent row the day is fixed; from Quick actions the admin picks it.
  const openAdd = (workDate = "", slackUserId = slackRef?.id === id ? slackRef.slackUserId : null) =>
    openEdit({ id: null, memberId: Number(id), slackUserId, workDate });
  // Export what's on screen: the active range if one is set, else the last 90 days.
  const exportMember = () =>
    downloadLogs(
      rangeStart || moment().subtract(90, "days").format("YYYY-MM-DD"),
      rangeEnd || moment().format("YYYY-MM-DD"),
      String(id)
    );
  const editToday = () => {
    // weekLogs, so this still works while the table is filtered to an older range.
    const today = weekLogs.find((l) => moment(l.workDate).isSame(moment(), "day"));
    if (today) openEdit(today);
    else toast("No entry for today yet.");
  };
  // Context-aware reminder: the backend inspects the member's current attendance
  // state and either DMs them the right nudge (sent=true) or reports there was
  // nothing to send (sent=false → info toast).
  const sendReminder = async () => {
    if (reminding) return;
    setReminding(true);
    const { status, sent, failed, message } = await sendReminderApi(id);
    setReminding(false);
    if (status !== 200) return toast.error("Couldn't send reminder");
    if (failed) return toast.error(message || "Couldn't deliver the reminder");
    if (sent) toast.success(message || `Reminder sent to ${member?.memberName || "member"}`);
    else toast(message || "No reminder needed.");
  };

  // Work nature drives reward-contest eligibility (only OFFICE staff compete).
  const changeNature = async (value) => {
    if (savingNature || value === member.workNature) return;
    setSavingNature(true);
    const { status } = await updateMember(id, { work_nature: value });
    setSavingNature(false);
    if (status === 200) { setMember((prev) => ({ ...prev, workNature: value })); toast.success("Work nature updated"); }
    else toast.error("Couldn't update work nature");
  };

  // Individual working hours: when enabled, this member follows their own schedule
  // instead of the global Settings > Working hours. Times are stored HH:mm:ss.
  const setHour = (k, v) => setHours((h) => ({ ...h, [k]: v }));
  // Numeric fields use type="text" + inputMode to avoid native <input type="number">
  // quirks (some browsers/locales blank the field while typing); keep digits only.
  const setHourNum = (k) => (e) => setHour(k, e.target.value.replace(/\D/g, ""));
  // Update one field (enabled | start | end) of one weekday's schedule.
  const setDay = (tok, field, val) =>
    setHours((h) => ({ ...h, day_schedules: { ...h.day_schedules, [tok]: { ...h.day_schedules[tok], [field]: val } } }));
  const saveHours = async () => {
    if (savingHours || !hours) return;
    setSavingHours(true);
    // One row per weekday; weekday = index + 1 (Sun=1 … Sat=7, MySQL DAYOFWEEK).
    const daySchedules = WH_DAYS.map((tok, i) => {
      const ds = hours.day_schedules[tok];
      return {
        weekday: i + 1,
        enabled: !!ds.enabled,
        day_start: ds.start ? `${ds.start}:00` : null,
        day_end: ds.end ? `${ds.end}:00` : null,
      };
    });
    const payload = {
      custom_hours_enabled: hours.enabled,
      grace_period_min: Number(hours.grace_period_min) || 0,
      day_schedules: daySchedules,
      reminder_signin_after_minutes: Number(hours.reminder_signin_after_minutes) || 0,
      reminder_long_break_min: Number(hours.reminder_long_break_min) || 0,
      reminder_signout_hours: Number(hours.reminder_signout_hours) || 0,
      reminder_signin_enabled: !!hours.reminder_signin_enabled,
      reminder_long_break_enabled: !!hours.reminder_long_break_enabled,
      reminder_signout_enabled: !!hours.reminder_signout_enabled,
    };
    const { status } = await updateMember(id, payload);
    setSavingHours(false);
    if (status === 200) {
      setMember((prev) => ({
        ...prev,
        customHoursEnabled: payload.custom_hours_enabled,
        gracePeriodMin: payload.grace_period_min,
        daySchedules: daySchedules.map((s) => ({ weekday: s.weekday, enabled: s.enabled, dayStart: s.day_start, dayEnd: s.day_end })),
        reminderSigninAfterMinutes: payload.reminder_signin_after_minutes,
        reminderLongBreakMin: payload.reminder_long_break_min,
        reminderSignoutHours: payload.reminder_signout_hours,
        reminderSigninEnabled: payload.reminder_signin_enabled,
        reminderLongBreakEnabled: payload.reminder_long_break_enabled,
        reminderSignoutEnabled: payload.reminder_signout_enabled,
      }));
      toast.success("Working hours updated");
    } else toast.error("Couldn't update working hours");
  };

  if (loading || !member) {
    return (
      <>
        <button className="profile-back" onClick={() => navigate("/members")}><svg><use href="#i-chev-l" /></svg>All members</button>
        <div className="profile-grid">
          <div className="profile-main">
            <div className="id-card"><span className="skeleton skeleton-circle" style={{ width: 84, height: 84 }} /><div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}><Skeleton width={180} height={20} /><Skeleton width={120} /><Skeleton width="60%" /></div></div>
            <div className="stat-row">{Array.from({ length: 8 }).map((_, i) => <div className="stat-box" key={i}><Skeleton width={70} /><div style={{ height: 8 }} /><Skeleton width={54} height={22} /></div>)}</div>
            <div className="rec-card"><div className="rec-head"><Skeleton width={160} height={18} /></div><SkeletonRows count={6} /></div>
          </div>
          <div className="profile-side"><div className="side-card"><SkeletonRows count={3} /></div></div>
        </div>
      </>
    );
  }

  const m = member;
  // Open break start for this member (from the active-breaks list), if they're on a break now.
  const memberBreakStart = onBreak.find((b) => String(b.memberId) === String(id))?.breakStart;
  const initials = (m.memberName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const username = (m.memberEmail || "").split("@")[0] || `${m.firstName || ""}.${m.lastName || ""}`.toLowerCase();
  const live = liveBits(m);

  return (
    // Clicking anywhere outside the date popover dismisses it (the popover's own wrapper
    // stops propagation). react-date-range selects on mousedown/mouseup, so a document-level
    // listener would also fire on the stray click that follows and shut the calendar after
    // the FIRST date — collapsing every range to a single day. Same pattern as History.
    <div onClick={() => setShowDatePicker(false)}>
      <button className="profile-back" onClick={() => navigate("/members")}><svg><use href="#i-chev-l" /></svg>All members</button>

      <div className="profile-grid">
        {/* LEFT */}
        <div className="profile-main">
          {/* Identity card */}
          <div className="id-card">
            <div className="id-cover" />
            {m.workNature && <span className="id-emp-tag">{WORK_NATURE_LABELS[m.workNature] || m.workNature}</span>}
            {m.memberImgUrl
              ? <img className="id-av av av-84" src={m.memberImgUrl} alt="" style={{ objectFit: "cover" }} />
              : <div className="id-av av av-84 av-MH">{initials}</div>}
            <div className="id-info">
              <div className="id-name-row">
                <span className="id-name">{m.memberName}</span>
                <span className={`status-pill ${live.cls}`}>{live.label}</span>
              </div>
              <div className="id-sub">{m.memberTitle} · @{username}</div>
              <div className="id-contacts">
                <span className="id-contact"><svg><use href="#i-mail" /></svg>{m.memberEmail || "—"}</span>
                <span className="id-contact"><svg><use href="#i-phone" /></svg>{m.memberPhone || "—"}</span>
                <span className="id-contact"><svg><use href="#i-globe" /></svg>{m.timezone || "—"}</span>
              </div>
            </div>
          </div>

          {/* Stat tiles */}
          <div className="stat-row">
            {/* <div className="stat-box"><div className="sb-label">Attendance rate</div><div className="sb-value" style={{ color: "#34D3A6" }}>{stats.rate}%</div><div className="sb-sub">{periodLabel}</div></div> */}
            {/* <div className="stat-box"><div className="sb-label">Hours</div><div className="sb-value" style={{ color: "var(--remote)" }}>{stats.hours}</div><div className="sb-sub">actual worked · {periodLabel}</div></div> */}
            <div className="stat-box"><div className="sb-label">Avg check-in</div><div className="sb-value" style={{ color: "var(--brand)" }}>{stats.avgInStr}</div><div className="sb-sub">{periodLabel}</div></div>
            <div className="stat-box"><div className="sb-label">Avg hours / day</div><div className="sb-value" style={{ color: "var(--brand)" }}>{stats.avgHours}</div><div className="sb-sub">{periodLabel}</div></div>
            <div className="stat-box" title="Signed in but never signed out — needs the sign-out added by hand">
              <div className="sb-label">Missed sign-outs</div>
              <div className="sb-value" style={{ color: "var(--amber)" }}>{String(stats.missedSignOuts).padStart(2, "0")}</div>
              <div className="sb-sub">{periodLabel}</div>
            </div>
            <div className="stat-box"><div className="sb-label">Late arrivals</div><div className="sb-value" style={{ color: "var(--pink)" }}>{String(stats.late).padStart(2, "0")}</div><div className="sb-sub">{periodLabel}</div></div>
            {/* <div className="stat-box"><div className="sb-label">Present days</div><div className="sb-value" style={{ color: "var(--green)" }}>{String(stats.presentDays).padStart(2, "0")}</div><div className="sb-sub">{periodLabel}</div></div> */}
            <div className="stat-box"><div className="sb-label">Office days</div><div className="sb-value" style={{ color: "var(--office)" }}>{String(stats.officeDays).padStart(2, "0")}</div><div className="sb-sub">{periodLabel}</div></div>
            <div className="stat-box"><div className="sb-label">Remote days</div><div className="sb-value" style={{ color: "var(--remote)" }}>{String(stats.remoteDays).padStart(2, "0")}</div><div className="sb-sub">{periodLabel}</div></div>
            <div className="stat-box"><div className="sb-label">Reminders</div><div className="sb-value" style={{ color: "var(--amber)" }}>{String(stats.reminderCount).padStart(2, "0")}</div><div className="sb-sub">{periodLabel}</div></div>
            <div className="stat-box"><div className="sb-label">Break</div><div className="sb-value" style={{ color: "var(--brand)" }}>{stats.breakHours}</div><div className="sb-sub">{stats.breakCount} break{stats.breakCount === 1 ? "" : "s"} · {periodLabel}</div></div>
          </div>

          {/* Recent attendance */}
          <div className="rec-card">
            <div className="rec-head">
              <div className="rec-title">Recent attendance</div>
              <div className="rec-tools">
                <button className="rec-period" onClick={exportMember} title="Export this member"><svg><use href="#i-download" /></svg>Export</button>
                {PERIODS.map((p) => (
                  <button key={p} className={`pchip ${period === p ? "on" : ""}`} onClick={() => setPeriod(p)}>{p}</button>
                ))}
                {/* Custom range — lets an admin check any earlier stretch of dates.
                    Picking dates switches the active period to CUSTOM, deselecting the chips. */}
                <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`btn btn-sm filter-trigger ${period === CUSTOM ? "on" : ""}`}
                    onClick={() => setShowDatePicker((v) => !v)}
                  >
                    <svg><use href="#i-cal" /></svg>{period === CUSTOM ? dateRangeLabel() : "Custom range"}
                  </button>
                  {showDatePicker && (
                    <div className="date-popover" style={{ position: "absolute", top: 40, right: 0, zIndex: 99, boxShadow: "var(--shadow)", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }}>
                      <DateRangePicker
                        onChange={(item) => { setDateRange([item.selection]); setPeriod(CUSTOM); }}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={dateRange}
                        direction="vertical"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Header + rows scroll together on narrow screens. */}
            <div className="table-scroll">
            <div className="rec-grid rec-colhead">
              <div>Date</div><div>In</div><div>Out</div><div>Hours</div><div>Break</div><div title="Reminders" aria-label="Reminders"><svg className="colhead-ic"><use href="#i-bell" /></svg></div><div>Type</div><div>Status</div><div></div>
            </div>

            {rows.length === 0 && <div className="rec-empty">No records in this period.</div>}
            {rows.map((log) => {
              if (!log.id) {
                // Absent day: nothing recorded, so only the date, any reminders sent, and "Add entry".
                const remKey = `absent-${log.workDate}`;
                const remOpen = reminderDetails.isOpen(remKey);
                return (
                  <React.Fragment key={remKey}>
                  <div className="rec-grid rec-row">
                    <div className="rec-date">{moment(log.workDate).format("MMMM D")}</div>
                    <div className="log-time">—</div>
                    <div className="log-time">—</div>
                    <div className="log-time">—</div>
                    <div className="log-break">—</div>
                    <div><ReminderCell count={log.reminderCount} rowKey={remKey} expanded={remOpen} onToggle={reminderDetails.toggle} /></div>
                    <div><span style={{ color: "var(--muted-2)" }}>—</span></div>
                    <div><span className="status-pill absent">Absent</span></div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button className="row-edit" title="Add entry" onClick={() => openAdd(log.workDate, log.slackUserId)}><svg><use href="#i-edit" /></svg></button>
                    </div>
                  </div>
                  {remOpen && <ReminderDetailRow breakdown={log.reminderBreakdown} />}
                  </React.Fragment>
                );
              }
              const remote = log.workType === "REMOTE";
              const today = moment(log.workDate).isSame(moment(), "day");
              const [sLabel, sCls] = logStatus(log);
              // Today's in-progress row ticks live; all other rows use the stored values.
              const now = moment();
              const liveBreakSec = liveBreakSeconds(log, memberBreakStart, now);
              const liveActualSec = liveActualSeconds(log, liveBreakSec, now);
              const breakStr = liveActualSec != null ? secToHhmm(liveBreakSec) : secToHm(timeToSec(log.totalBreakDuration));
              const hoursStr = liveActualSec != null ? secToHhmm(liveActualSec) : secToHm(timeToSec(log.actualWorkingHour || log.workingHour));
              const brkOpen = breakDetails.isOpen(log.id);
              const remOpen = reminderDetails.isOpen(log.id);
              return (
                <React.Fragment key={log.id}>
                <div className={`rec-grid rec-row ${today ? "today" : ""}`}>
                  <div className="rec-date">{today ? "Today" : moment(log.workDate).format("MMMM D")}</div>
                  <div className="log-time">{fmtTime(log.checkIn) || "—"}</div>
                  <div className="log-time">{log.checkout ? fmtTime(log.checkout) : "—"}{isOvernight(log) && (<span className="ovn-tip" title="Overnight shift — checked out the next day"><svg><use href="#i-info" /></svg></span>)}</div>
                  <div className="log-time"><HoursCell log={log} hoursStr={hoursStr} /></div>
                  <div className="log-break"><BreakCell log={log} breakStr={breakStr} expanded={brkOpen} onToggle={breakDetails.toggle} /></div>
                  <div><ReminderCell count={log.reminderCount} rowKey={log.id} expanded={remOpen} onToggle={reminderDetails.toggle} /></div>
                  <div><span className="pill-type" style={{ color: `var(--${remote ? "remote" : "office"})`, background: `var(--${remote ? "remote" : "office"}-bg)` }}><svg><use href={`#i-${remote ? "wifi" : "building"}`} /></svg>{remote ? "Remote" : "Office"}</span></div>
                  <div><span className={`status-pill ${sCls}`}>{sLabel}</span></div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button className="row-edit" title="Edit entry" onClick={() => openEdit(log)}><svg><use href="#i-edit" /></svg></button>
                  </div>
                </div>
                {brkOpen && <BreakDetailRow breaks={breakDetails.breaksById[log.id]} loading={breakDetails.loadingId === log.id} />}
                {remOpen && <ReminderDetailRow breakdown={log.reminderBreakdown} />}
                </React.Fragment>
              );
            })}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="profile-side">
          <div className="side-card">
            <div className="tw-title">Employment</div>
            <div className="tw-sub">Work nature — only office staff join the reward contest</div>
            <select
              className="set-select"
              style={{ width: "100%" }}
              value={m.workNature || "OFFICE"}
              disabled={savingNature}
              onChange={(e) => changeNature(e.target.value)}
            >
              {WORK_NATURE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Individual working hours — overrides the global company schedule for this member. */}
          <div className="side-card">
            <div className="tw-title">Working Hours</div>
            <div className="tw-sub">Personal schedule — overrides the company default</div>
            <label className="wh-toggle">
              <input
                type="checkbox"
                checked={!!hours?.enabled}
                disabled={savingHours || !hours}
                onChange={(e) => setHour("enabled", e.target.checked)}
              />
              <span>Use individual working hours</span>
            </label>
            {hours?.enabled && (
              <div className="wh-body">
                <label className="wh-days-label">Weekly schedule</label>
                <div className="wh-week">
                  {WH_DAYS.map((d) => {
                    const ds = hours.day_schedules[d];
                    // Off day: the whole card is an "Add" button that enables the day.
                    if (!ds.enabled) {
                      return (
                        <button type="button" key={d} className="wh-day-row off" onClick={() => setDay(d, "enabled", true)} title={`Add ${d} schedule`}>
                          <span className="wh-day-name">{d}</span>
                          <span className="wh-unavail">Unavailable</span>
                          <span className="wh-add" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                          </span>
                        </button>
                      );
                    }
                    // Active day: editable start–end, with a hover-revealed remove (×).
                    return (
                      <div className="wh-day-row on" key={d}>
                        <span className="wh-day-name">{d}</span>
                        <div className="wh-times">
                          <input type="time" className="set-input mono" value={ds.start} onChange={(e) => setDay(d, "start", e.target.value)} />
                          <span className="wh-dash">–</span>
                          <input type="time" className="set-input mono" value={ds.end} onChange={(e) => setDay(d, "end", e.target.value)} />
                        </div>
                        <button type="button" className="wh-remove" onClick={() => setDay(d, "enabled", false)} title={`Remove ${d}`} aria-label={`Remove ${d}`}>
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="wh-grid">
                  <div className="set-field"><label>Sign-in grace period (min)</label><input type="text" inputMode="numeric" className="set-input mono" value={hours.grace_period_min} onChange={setHourNum("grace_period_min")} /></div>
                </div>

                <div className="wh-subhead">Reminders</div>
                <div className="wh-grid">
                  <div className="set-field">
                    <label className="set-field-toggle">
                      <input type="checkbox" checked={!!hours.reminder_signin_enabled} onChange={(e) => setHour("reminder_signin_enabled", e.target.checked)} />
                      <span>Sign-in (min after day start)</span>
                    </label>
                    <input type="text" inputMode="numeric" className="set-input mono" value={hours.reminder_signin_after_minutes} onChange={setHourNum("reminder_signin_after_minutes")} disabled={!hours.reminder_signin_enabled} />
                  </div>
                  <div className="set-field">
                    <label className="set-field-toggle">
                      <input type="checkbox" checked={!!hours.reminder_long_break_enabled} onChange={(e) => setHour("reminder_long_break_enabled", e.target.checked)} />
                      <span>Long break (min)</span>
                    </label>
                    <input type="text" inputMode="numeric" className="set-input mono" value={hours.reminder_long_break_min} onChange={setHourNum("reminder_long_break_min")} disabled={!hours.reminder_long_break_enabled} />
                  </div>
                  <div className="set-field">
                    <label className="set-field-toggle">
                      <input type="checkbox" checked={!!hours.reminder_signout_enabled} onChange={(e) => setHour("reminder_signout_enabled", e.target.checked)} />
                      <span>Sign-out after (hrs)</span>
                    </label>
                    <input type="text" inputMode="numeric" className="set-input mono" value={hours.reminder_signout_hours} onChange={setHourNum("reminder_signout_hours")} disabled={!hours.reminder_signout_enabled} />
                  </div>
                </div>
              </div>
            )}
            <button className="qa-btn save wh-save" onClick={saveHours} disabled={savingHours || !hours}>
              <svg><use href="#i-clock" /></svg>{savingHours ? "Saving…" : "Save working hours"}
            </button>
          </div>

          <div className="side-card">
            <div className="tw-title">This week</div>
            <div className="tw-sub">Daily hours vs 8h target</div>
            <div className="tw-chart">
              {week.map((w, i) => (
                <div className="tw-col" key={i}>
                  <div className="tw-bar" style={{ height: `${Math.max(4, w.h)}%` }} title={w.day} />
                  <div className="tw-day">{w.day}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="side-card">
            <div className="qa-title">Quick actions</div>
            <button className="qa-btn" onClick={() => openAdd()}><svg><use href="#i-plus" /></svg>Add entry</button>
            <button className="qa-btn" onClick={editToday}><svg><use href="#i-edit" /></svg>Edit today's entry</button>
            <button className="qa-btn" onClick={exportMember}><svg><use href="#i-download" /></svg>Export this member</button>
            <button className="qa-btn" onClick={sendReminder} disabled={reminding}><svg><use href="#i-bell" /></svg>{reminding ? "Sending…" : "Send reminder"}</button>
          </div>
        </div>
      </div>

      {editing && (
        <EditEntryModal
          log={editing}
          onClose={() => setEditing(null)}
          onSaved={applyUpdatedLog}
          onCreated={() => setReloadKey((k) => k + 1)}
          onDelete={(log) => { setEditing(null); handleDelete(log); }}
        />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
};

export default MemberProfile;
